/**
 * Page control for long record lists. Card-footer variant (numbers) and
 * simple variant (prev/next + count).
 * Spec: assets/handoffs/design_system_export/specs/Pagination.json
 *
 * @param {'numbered'|'simple'=numbered} variant
 * @param {number} page - 1-indexed current page
 * @param {number} totalPages
 * @param {function} onPageChange - (nextPage) => void
 */
import React from "react";
import classNames from "classnames";
import Button from "../Button/Button";
import Icon from "../icons/Icon";
import styles from "./Pagination.module.scss";

const getPageList = (page, totalPages, siblingCount = 1) => {
  const totalNumbers = siblingCount * 2 + 5;
  if (totalPages <= totalNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(page - siblingCount, 1);
  const rightSibling = Math.min(page + siblingCount, totalPages);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  const pages = [1];
  if (showLeftEllipsis) pages.push("ellipsis-left");
  for (let p = leftSibling; p <= rightSibling; p++) {
    if (p !== 1 && p !== totalPages) pages.push(p);
  }
  if (showRightEllipsis) pages.push("ellipsis-right");
  if (totalPages !== 1) pages.push(totalPages);
  return pages;
};

const Pagination = ({ variant = "numbered", page, totalPages, onPageChange, className, ...rest }) => {
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const prevButton = (
    <Button
      hierarchy="secondaryGray"
      size="sm"
      iconLeading={<Icon name="ArrowLeft" />}
      disabled={!canPrev}
      onClick={() => onPageChange(page - 1)}
    >
      Previous
    </Button>
  );

  const nextButton = (
    <Button
      hierarchy="secondaryGray"
      size="sm"
      iconTrailing={<Icon name="ArrowRight" />}
      disabled={!canNext}
      onClick={() => onPageChange(page + 1)}
    >
      Next
    </Button>
  );

  if (variant === "simple") {
    return (
      <nav aria-label="Pagination" className={classNames(styles.root, className)} {...rest}>
        {prevButton}
        <span className={styles.countText}>
          Page {page} of {totalPages}
        </span>
        {nextButton}
      </nav>
    );
  }

  const pages = getPageList(page, totalPages);

  return (
    <nav aria-label="Pagination" className={classNames(styles.root, className)} {...rest}>
      {prevButton}
      <div className={styles.pageButtons}>
        {pages.map((p) =>
          typeof p === "number" ? (
            <button
              key={p}
              type="button"
              className={classNames(styles.pageButton, {
                [styles["pageButton--current"]]: p === page,
              })}
              aria-current={p === page ? "page" : undefined}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          ) : (
            <span key={p} className={styles.ellipsis} aria-hidden="true">
              …
            </span>
          )
        )}
      </div>
      {nextButton}
    </nav>
  );
};

export default Pagination;
